"use client";
import { termsData } from "@/data/terms-data";
import { useStore } from "@/state-management/context";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { startTransition, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaGavel, FaRegClock } from "react-icons/fa";
import { IoIosArrowUp } from "react-icons/io";
import { ContentItem, sectionIcons, SectionsContent } from "./terms-helper";

/* how far from the viewport top the sidebar should stick (below the navbar) */
const STICK_TOP = 96;

/* ─── Main Component ─── */
export default function Terms() {
  const locale = useLocale();
  const t = useTranslations("Terms");
  const isArabic = locale === "ar";
  const data = isArabic ? termsData.ar : termsData.en;

  const { termsTab, setTermsTab } = useStore();
  const [activeTab, setActiveTab] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);

  const contentRef = useRef<HTMLElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  /* fixed-position fallback state (works even if an ancestor breaks CSS sticky) */
  const [pin, setPin] = useState<{ mode: "static" | "fixed" | "bottom"; left: number; width: number; height: number }>({
    mode: "static",
    left: 0,
    width: 0,
    height: 0,
  });

  /* open the tab requested by Footer links via store */
  useEffect(() => {
    if (termsTab === null) return;
    startTransition(() => setActiveTab(termsTab));
    setTermsTab(null);
    setTimeout(() => scrollToContent(), 120);
  }, [termsTab, setTermsTab]);

  const scrollToContent = () => {
    const el = contentRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - STICK_TOP + 8;
    window.scrollTo({ top: y, behavior: "smooth" });
  };
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  /* scroll: progress bar + back-to-top + sidebar pinning */
  const measure = useCallback(() => {
    const aside = asideRef.current;
    const row = rowRef.current;
    const card = cardRef.current;
    if (!aside || !row || !card) return;
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

    // reading progress of the whole page
    const doc = document.documentElement;
    const max = doc.scrollHeight - window.innerHeight;
    setProgress(max > 0 ? Math.min(100, Math.round((window.scrollY / max) * 100)) : 0);
    setScrolled(window.scrollY > 280);

    if (!isDesktop) {
      setPin((p) => (p.mode === "static" ? p : { ...p, mode: "static" }));
      return;
    }

    const asideRect = aside.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const cardH = card.offsetHeight;
    const left = asideRect.left;
    const width = asideRect.width;

    // sidebar's natural top has scrolled above the stick line → pin it
    const shouldPin = asideRect.top <= STICK_TOP;
    // stop pinning when the pinned card would overflow the bottom of the row
    const reachedBottom = rowRect.bottom - STICK_TOP <= cardH;

    if (shouldPin && !reachedBottom) {
      setPin({ mode: "fixed", left, width, height: cardH });
    } else if (shouldPin && reachedBottom) {
      setPin({ mode: "bottom", left, width, height: cardH });
    } else {
      setPin((p) => (p.mode === "static" ? p : { ...p, mode: "static" }));
    }
  }, []);

  useLayoutEffect(() => {
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [measure, activeTab]);

  const handleTabChange = (idx: number) => {
    setActiveTab(idx);
    requestAnimationFrame(scrollToContent);
  };

  const active = data[activeTab] as {
    title: string;
    main?: string;
    content?: string[];
    sections?: { subtitle: string; items: string[] }[];
  };
  const total = data.length;
  const Arrow = isArabic ? FaChevronLeft : FaChevronRight;

  /* sidebar card style depending on pin mode */
  const cardStyle: React.CSSProperties =
    pin.mode === "fixed"
      ? { position: "fixed", top: STICK_TOP, left: pin.left, width: pin.width, maxHeight: `calc(100vh - ${STICK_TOP + 24}px)` }
      : pin.mode === "bottom"
        ? { position: "absolute", bottom: 0, left: 0, width: pin.width, maxHeight: `calc(100vh - ${STICK_TOP + 24}px)` }
        : { maxHeight: `calc(100vh - ${STICK_TOP + 24}px)` };

  return (
    <div className={`${isArabic ? "font-cairo rtl" : "font-sans ltr"} relative min-h-screen bg-[#F6F7FB] pb-28 text-slate-900`}>
      {/* ── Reading progress ── */}
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-1 bg-transparent">
        <div className="h-full bg-gradient-to-l from-[#0d3589] via-[#d9b25a] to-[#0d3589] transition-[width] duration-150" style={{ width: `${progress}%` }} />
      </div>

      {/* ── Ambient blobs ── */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/3 size-125 rounded-full bg-[#0d3589]/5 blur-[160px]" />
        <div className="absolute -bottom-40 right-1/3 size-125 rounded-full bg-[#d9b25a]/10 blur-[160px]" />
      </div>

      {/* ── Hero ── */}
      <section className="relative border-b border-slate-100 bg-white pt-6 pb-12">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#d9b25a]/60 to-transparent" />
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }} className="mx-auto mb-5 grid size-16 place-items-center rounded-2xl bg-gradient-to-br from-[#0d3589] to-[#071f52] text-[22px] text-[#d9b25a] shadow-[0_18px_40px_-14px_rgba(13,53,137,0.6)]">
            <FaGavel />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.4 }} className="mb-3 text-4xl font-black tracking-tight text-slate-900 md:text-5xl">
            {t("legalCenter")}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22, duration: 0.4 }} className="mx-auto max-w-xl text-base text-slate-400">
            {t("description")}
          </motion.p>
        </div>
      </section>

      {/* ── Body ── */}
      <div className="container mx-auto mt-8 px-4">
        <div ref={rowRef} className="relative flex flex-col gap-6 lg:flex-row lg:items-start">
          {/* ══════════ SIDEBAR (sticky + fixed fallback) ══════════ */}
          <aside ref={asideRef} className="relative shrink-0 lg:w-72 xl:w-80" style={{ minHeight: pin.mode !== "static" ? pin.height : undefined }}>
            <div
              ref={cardRef}
              style={cardStyle}
              className="z-20 flex flex-col overflow-hidden rounded-[28px] border border-slate-100 bg-white shadow-[0_18px_50px_-22px_rgba(15,23,42,0.25)] transition-shadow"
            >
              {/* header with progress ring */}
              <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-[#0a1128] via-[#0d1f4d] to-[#071433] px-5 py-4 text-white">
                <div className="pointer-events-none absolute -top-10 -right-8 size-32 rounded-full bg-[#d9b25a]/20 blur-2xl" />
                <div className="relative flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-100/60">{t("agreementSections")}</p>
                    <p className="mt-0.5 text-sm font-black">
                      {isArabic ? `القسم ${activeTab + 1} من ${total}` : `Section ${activeTab + 1} of ${total}`}
                    </p>
                  </div>
                  <div className="relative grid size-11 place-items-center">
                    <svg viewBox="0 0 36 36" className="absolute inset-0 -rotate-90">
                      <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15.5" fill="none" stroke="#d9b25a" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${((activeTab + 1) / total) * 97.4} 97.4`} />
                    </svg>
                    <span className="text-[11px] font-black text-[#d9b25a]">{Math.round(((activeTab + 1) / total) * 100)}%</span>
                  </div>
                </div>
              </div>

              {/* list */}
              <nav className="flex-1 overflow-y-auto overscroll-contain p-2.5 [scrollbar-width:thin]">
                {data.map((section, idx) => {
                  const isActive = activeTab === idx;
                  const done = idx < activeTab;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleTabChange(idx)}
                      className={`group relative mb-1 flex w-full cursor-pointer items-center gap-3 rounded-2xl px-3 py-2.5 text-start transition-all duration-200 ${
                        isActive ? "bg-gradient-to-l from-[#0d3589]/10 to-transparent text-[#0d3589]" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                      }`}
                    >
                      {isActive && (
                        <motion.span layoutId="activePill" className={`absolute ${isArabic ? "right-0" : "left-0"} top-1/2 h-8 w-[3px] -translate-y-1/2 rounded-full bg-gradient-to-b from-[#d9b25a] to-[#0d3589]`} transition={{ type: "spring", stiffness: 500, damping: 35 }} />
                      )}
                      <span className={`grid size-9 shrink-0 place-items-center rounded-xl text-[13px] transition-all duration-200 ${
                        isActive ? "bg-gradient-to-br from-[#0d3589] to-[#071f52] text-[#d9b25a] shadow-md shadow-[#0d3589]/25" : done ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400 group-hover:bg-[#0d3589]/8 group-hover:text-[#0d3589]"
                      }`}>
                        {sectionIcons[idx]}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[10px] font-black text-slate-300">{isArabic ? `${idx + 1} / ${total}` : `${idx + 1} / ${total}`}</span>
                        <span className="line-clamp-1 text-[12.5px] font-bold leading-snug">{section.title}</span>
                      </span>
                      <Arrow className={`shrink-0 text-[9px] transition-all duration-200 ${isActive ? "opacity-100 text-[#d9b25a]" : "opacity-0 group-hover:opacity-40"}`} />
                    </button>
                  );
                })}
              </nav>

              {/* quick actions */}
              <div className="shrink-0 grid grid-cols-2 gap-2 border-t border-slate-100 p-3">
                <button onClick={() => window.print()} className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] font-black text-slate-600 transition-colors hover:bg-slate-100">
                  {isArabic ? "طباعة" : "Print"}
                </button>
                <button
                  onClick={() => navigator.clipboard?.writeText(window.location.href)}
                  className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] font-black text-slate-600 transition-colors hover:bg-slate-100"
                >
                  {isArabic ? "نسخ الرابط" : "Copy link"}
                </button>
              </div>
            </div>
          </aside>

          {/* ══════════ CONTENT ══════════ */}
          <main ref={contentRef} className="min-w-0 flex-1">
            <AnimatePresence mode="wait">
              <motion.article
                key={activeTab}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.26, ease: "easeOut" }}
                className="rounded-[2.5rem] border border-slate-100 bg-white p-7 shadow-[0_18px_50px_-22px_rgba(15,23,42,0.18)] md:p-11"
              >
                <header className="mb-9">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="grid size-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#0d3589] to-[#071f52] text-[#d9b25a]">{sectionIcons[activeTab]}</div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-[#0d3589]">
                      {t("sectionNumber")} {activeTab + 1}
                    </p>
                  </div>
                  <h2 className="text-2xl font-black leading-snug text-slate-900 md:text-3xl">{active.title}</h2>
                  <div className="mt-5 h-px w-full bg-gradient-to-r from-[#d9b25a]/60 via-slate-100 to-transparent" />
                </header>

                {active.main && (
                  <div className="mb-8 rounded-2xl border border-[#0d3589]/10 border-s-[3px] border-s-[#d9b25a] bg-[#0d3589]/[0.03] p-5 text-base leading-relaxed text-slate-700">
                    {active.main}
                  </div>
                )}

                <div className="space-y-3">
                  {active.content?.map((item, i) => <ContentItem key={i} item={item} />)}
                  {active.sections && <SectionsContent sections={active.sections} />}
                </div>

                {/* prev / next */}
                <nav className="mt-12 grid gap-3 border-t border-slate-100 pt-7 sm:grid-cols-2">
                  {activeTab > 0 ? (
                    <button onClick={() => handleTabChange(activeTab - 1)} className="group flex items-center gap-3 rounded-2xl border border-slate-200 p-4 text-start transition-all hover:border-[#0d3589]/40 hover:bg-slate-50">
                      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 group-hover:bg-[#0d3589] group-hover:text-white"><FaChevronRight className={isArabic ? "" : "rotate-180"} size={11} /></span>
                      <span className="min-w-0"><span className="block text-[10px] font-black text-slate-400">{isArabic ? "السابق" : "Previous"}</span><span className="line-clamp-1 text-sm font-bold text-slate-800">{data[activeTab - 1].title}</span></span>
                    </button>
                  ) : <span />}
                  {activeTab < total - 1 && (
                    <button onClick={() => handleTabChange(activeTab + 1)} className="group flex items-center justify-between gap-3 rounded-2xl bg-gradient-to-br from-[#0d3589] to-[#071f52] p-4 text-start text-white shadow-lg shadow-[#0d3589]/25 transition-all hover:brightness-110">
                      <span className="min-w-0"><span className="block text-[10px] font-black text-blue-100/70">{isArabic ? "التالي" : "Next"}</span><span className="line-clamp-1 text-sm font-bold">{data[activeTab + 1].title}</span></span>
                      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-white/15 text-[#d9b25a]"><FaChevronLeft className={isArabic ? "" : "rotate-180"} size={11} /></span>
                    </button>
                  )}
                </nav>

                <footer className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-slate-100 pt-6 sm:flex-row">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <FaRegClock />
                    <span>{`${t("lastUpdated")}: ${new Date().getFullYear()}`}</span>
                  </div>
                  <button onClick={scrollToTop} className="flex cursor-pointer items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition-all duration-200 hover:bg-[#0d3589] active:scale-95">
                    <IoIosArrowUp size={16} />
                    <span>{t("backToTop") ?? "Back to top"}</span>
                  </button>
                </footer>
              </motion.article>
            </AnimatePresence>

            {/* CTA */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="group relative mt-8 overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#0a1128] via-[#0d1f4d] to-[#071433] p-8 text-white">
              <div className="absolute -top-10 -right-10 size-52 rounded-full bg-[#d9b25a] opacity-15 blur-[60px] transition-opacity duration-500 group-hover:opacity-25" />
              <div className="relative z-10 flex flex-col items-center justify-between gap-5 sm:flex-row">
                <div>
                  <h3 className="mb-1 text-xl font-black">{t("help")}</h3>
                  <p className="text-sm text-blue-100/60">{t("contact")}</p>
                </div>
                <Link href="/contact-us" className="shrink-0 rounded-xl bg-gradient-to-br from-[#d9b25a] to-[#b8923f] px-7 py-3.5 text-sm font-black text-[#0a1128] shadow-lg transition-all duration-200 hover:brightness-110 active:scale-95">
                  {t("contactUS")}
                </Link>
              </div>
            </motion.div>
          </main>
        </div>
      </div>

      {/* floating back to top */}
      <AnimatePresence>
        {scrolled && (
          <motion.button
            initial={{ opacity: 0, scale: 0.6, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 16 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className="fixed bottom-8 inset-e-6 z-50 grid size-12 cursor-pointer place-items-center rounded-full bg-gradient-to-br from-[#0d3589] to-[#071f52] text-[#d9b25a] shadow-xl shadow-[#0d3589]/30 transition-all duration-200 hover:scale-110 active:scale-95"
            aria-label="Scroll to top"
          >
            <IoIosArrowUp size={20} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
